import type { ChangeEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { DropdownContent } from "@similarweb/ui-components";
import { PopupClickContainer } from "@similarweb/ui-components";

import { NEW_ACCOUNT_VALUE } from "app/pages/sales-intelligence/domain/crm-integration/constants/account";
import type { CrmAccountRef } from "app/pages/sales-intelligence/domain/crm-integration/types/account";
import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import type {
    Option } from "./options";
import {
    accountsToOptions,
    allAccountsGroupOption,
    suggestedAccountsGroupOption,
    newAccountOption,
} from "./options";
import {
    StyledAccountsGroupItem,
    StyledAccountItem,
    StyledAccountSelect,
    StyledNewAccountLabel,
    StyledNewAccountItem,
    StyledExistAsLeadLabel,
    StyledDropdownButton,
    StyledDropdownDisabledButton,
    StyledDotsLoader,
} from "./styles";
import { useSearchAccountsOptions } from "./useSearchAccountsOptions";


type AccountSelectProps = {
    contactsCompanyName: string;
    integrationName: CrmIntegrationProvider;
    suggestedAccounts: readonly CrmAccountRef[];
    onChange: (value: string, searchedLabel?: string) => void;
    value: string | undefined;
    canCreateNewAccount: boolean;
    containerClassName: string;
    existAsLead?: boolean;
    searchedCompanyLabel?: string;
    onTrackSearch?: () => void;
    onTrackSelect?: (isNewAccount: boolean) => void;
};

export const AccountSelect = (props: AccountSelectProps) => {
    const {
        contactsCompanyName,
        integrationName,
        suggestedAccounts,
        onChange,
        value,
        canCreateNewAccount,
        containerClassName,
        existAsLead,
        searchedCompanyLabel,
        onTrackSearch,
        onTrackSelect,
    } = props;
    const translate = useTranslation();
    const [buttonLabel, setButtonLabel] = useState(() =>
        translate(`si.advanced_search.account_select.placeholder.${integrationName}`),
    );
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const popupContainerRef = useRef<PopupClickContainer>(null);
    const scrollAreaRef = useRef<{ scrollArea: { scrollTop(): void } }>(null);

    const suggestedOptions = accountsToOptions(suggestedAccounts);
    const hasSearch = searchTerm.length > 0;
    const visibleSuggestedOptions = hasSearch ? [] : suggestedOptions;
    const isNewAccount = value === NEW_ACCOUNT_VALUE;

    const { isLoading, searchedOptions } = useSearchAccountsOptions({
        isOpen,
        integrationName,
        searchTerm,
        containerClassName,
    });

    const getOptions = () => {
        let options: Option[] = [];

        if (visibleSuggestedOptions.length > 0) {
            options.push(suggestedAccountsGroupOption);
            const mappedWithIsSuggested = visibleSuggestedOptions.map((option) => ({
                ...option,
                isSuggested: true,
            }));
            options = options.concat(mappedWithIsSuggested);
        }

        if (visibleSuggestedOptions.length > 0 && searchedOptions.length > 0) {
            options.push(allAccountsGroupOption);
        }

        options = options.concat(searchedOptions);

        return options;
    };

    const renderOptions = () => {
        const options = getOptions();

        if (options.length === 0) {return <></>;}

        const hasGroupItem = options.some((option) => option.isGroupItem);

        return options.map((option) => {
            if (option.isGroupItem) {
                return (
                    <StyledAccountsGroupItem
                        id={option.value}
                        key={option.value}
                        preventDefault
                        iconName="category"
                        iconSize="sm"
                    >
                        {translate(`${option.label}.${integrationName}`)}
                    </StyledAccountsGroupItem>
                );
            }

            return (
                <StyledAccountItem
                    key={option.isSuggested ? `${option.value}_suggested` : option.value}
                    id={option.value}
                    onClick={() => handleSelect(option)}
                    selected={value === option.value}
                    preventDefault
                    extendedPadding={hasGroupItem}
                >
                    {option.label}
                </StyledAccountItem>
            );
        });
    };

    const getButtonLabel = (value: string | undefined): string => {
        if (!value) {return translate(`si.advanced_search.account_select.placeholder.${integrationName}`);}

        if (isNewAccount) {return contactsCompanyName;}

        const label = searchedOptions.concat(suggestedOptions).find((option) => option.value === value)?.label;

        return label ?? searchedCompanyLabel ?? "";
    };

    useEffect(() => setButtonLabel(getButtonLabel(value)), [value]);

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
        onTrackSearch?.();
        setSearchTerm(event.target.value);
        scrollAreaRef.current?.scrollArea.scrollTop();
    };

    const handleSelect = (option: Option) => {
        onTrackSelect?.(option.value === NEW_ACCOUNT_VALUE);
        onChange(option.value, option.isSuggested ? undefined : option.label);
        popupContainerRef.current?.closePopup();
    };

    const handleToggle = (newIsOpen: boolean) => {
        setIsOpen(newIsOpen);

        if (newIsOpen) {
            setSearchTerm("");
        }
    };

    const hasSuggestedAccounts = suggestedAccounts.length > 0;

    return (
        <StyledAccountSelect>
            {isNewAccount && !existAsLead ? <StyledNewAccountLabel>
                    {translate(`si.advanced_search.account_select.new_account.${integrationName}`)}
                </StyledNewAccountLabel> : null}
            {existAsLead ? <StyledExistAsLeadLabel>
                    {translate("si.advanced_search.account_select.exist_as_lead")}
                </StyledExistAsLeadLabel> : null}
            <PopupClickContainer
                appendTo={`.${containerClassName}`}
                ref={popupContainerRef}
                config={{
                    enabled: !existAsLead,
                    placement: "ontop-left",
                    autoPlacement: true,
                    cssClassContainer: "DropdownContent-container",
                    width: "233px",
                    onToggle: handleToggle,
                }}
                content={() => (
                    <DropdownContent
                        hasSearch
                        onSearch={handleSearch}
                        searchPlaceHolder={translate(
                            `si.advanced_search.account_select.search_placeholder.${integrationName}`,
                        )}
                        onClick={() => {}}
                        headerComponent={isLoading ? <StyledDotsLoader /> : null}
                        scrollAreaHeight={240}
                        scrollAreaRef={(ref) => (scrollAreaRef.current = ref)}
                    >
                        {canCreateNewAccount ? (
                            <StyledNewAccountItem
                                id={NEW_ACCOUNT_VALUE}
                                iconName={hasSuggestedAccounts ? "plus" : undefined}
                                iconSize="xs"
                                preventDefault
                                onClick={() => handleSelect(newAccountOption)}
                                selected={isNewAccount}
                            >
                                {hasSuggestedAccounts
                                    ? translate(
                                          `si.advanced_search.account_select.new_account_label.${integrationName}`,
                                      )
                                    : contactsCompanyName}
                            </StyledNewAccountItem>
                        ) : (
                            <></>
                        )}
                        {renderOptions()}
                    </DropdownContent>
                )}
            >
                <div>
                    {existAsLead ? (
                        <StyledDropdownDisabledButton key="dropdown-button" />
                    ) : (
                        <StyledDropdownButton key="dropdown-button">{buttonLabel}</StyledDropdownButton>
                    )}
                </div>
            </PopupClickContainer>
        </StyledAccountSelect>
    );
};
